import './FirstRoundRef.css';
import type { FirstRoundResults } from '../../contracts/static';
import { t } from '../../copy/strings';
import { fmtPct } from '../../format/numbers';
import type { Candidate } from '../../model/types';
import { useLayout } from '../../state/hooks';
import { partyClass, partyOf } from '../../theme/parties';

/** Um candidato na linha "1º turno": fatia dos válidos de 04/10, sem renormalizar para os dois (§7.8 bloco 3). */
export interface FirstRoundEntry {
  candidate: Pick<Candidate, 'number' | 'display_name' | 'party_code'>;
  share: number;
}

export interface FirstRoundRefProps {
  /** Os dois candidatos; `null` (dado ausente ou carregando) não renderiza nada nem reserva espaço (AC-7.8-02). */
  entries: readonly [FirstRoundEntry, FirstRoundEntry] | null;
  /** Duelo de governador (§8.3): "1º turno ■ 40,1% ■ 35,2%", sem os nomes visíveis. */
  compact?: boolean;
}

/**
 * Fatias do presidente no Brasil no 1º turno para o par do 2º turno (§7.8 bloco 3): `FirstRoundResults.president.national`
 * na ordem de `candidates` ([13, 22]). `null` se o arquivo não chegou (undefined), faltou (null) ou não traz um dos dois.
 */
export function presidentFirstRound(
  fr: FirstRoundResults | null | undefined,
  candidates: readonly [Candidate, Candidate],
): [FirstRoundEntry, FirstRoundEntry] | null {
  const shares = fr?.president.national.shares;
  if (!shares) return null;
  const a = shares[candidates[0].number];
  const b = shares[candidates[1].number];
  if (typeof a !== 'number' || typeof b !== 'number' || !Number.isFinite(a) || !Number.isFinite(b)) return null;
  return [
    { candidate: candidates[0], share: a },
    { candidate: candidates[1], share: b },
  ];
}

/** Ordem de número (N01): nunca a de votos. */
function byNumber(entries: readonly [FirstRoundEntry, FirstRoundEntry]): readonly [FirstRoundEntry, FirstRoundEntry] {
  const [a, b] = entries;
  const na = Number(a.candidate.number);
  const nb = Number(b.candidate.number);
  return Number.isFinite(na) && Number.isFinite(nb) && na > nb ? [b, a] : entries;
}

/**
 * Linha "1º turno" (§7.8): referência fixa do 1º turno, igual ao vivo, no replay e na simulação. Quadrados de cor
 * `aria-hidden` (§15.4.3 regra 6); o prefixo `.sr-only` repete o `title` (AC de §7.8 bloco 7) e a frase acessível
 * separa os dois por vírgula. Oculta na TV (não renderizada, AC-7.8-03).
 */
export function FirstRoundRef({ entries, compact = false }: FirstRoundRefProps) {
  const tv = useLayout().mode === 'tv';
  if (entries === null || tv) return null;
  const pair = byNumber(entries);
  return (
    <p
      class={compact ? 'first-round-ref first-round-ref--compact' : 'first-round-ref'}
      data-testid="first-round-ref"
      title={t('firstRound.title')}
    >
      <span class="sr-only">{t('firstRound.sr')} </span>
      <span class="first-round-ref__label" aria-hidden="true">
        {t('firstRound.label')}
      </span>
      {pair.map((e, i) => {
        const pct = fmtPct(e.share, 1);
        return (
          <span
            key={i}
            class={`first-round-ref__item ${partyClass(partyOf(e.candidate))}`}
            data-number={e.candidate.number}
          >
            {i > 0 && <span class="sr-only">, </span>}
            <span class="first-round-ref__swatch" data-dataviz="" aria-hidden="true" />
            {compact ? (
              <>
                <span class="sr-only">{e.candidate.display_name} </span>
                {pct}
              </>
            ) : (
              t('firstRound.item', { NOME: e.candidate.display_name, P: pct })
            )}
          </span>
        );
      })}
    </p>
  );
}
